import type { Theme } from "./themes";

/** The colours an accent sets: buttons, selections, and the chords unless they have a colour of their own. */
export type AccentColors = Pick<Theme, "accent" | "accentHover" | "accentSoft" | "accentSofter" | "onAccent" | "chord">;

/** An accent colour to choose in the appearance settings, in a light and a dark version. */
export type Accent = { label: string; light: AccentColors; dark: AccentColors };

export const ACCENTS = {
    blue: {
        label: "Blue",
        light: {
            accent: "#2F49C4",
            accentHover: "#22379A",
            accentSoft: "#EBEEFB",
            accentSofter: "#F5F7FE",
            onAccent: "#FFFFFF",
            chord: "#2F49C4",
        },
        dark: {
            accent: "#8EA2FF",
            accentHover: "#B0BEFF",
            accentSoft: "#232A4A",
            accentSofter: "#1B2038",
            onAccent: "#0E1330",
            chord: "#9FB0FF",
        },
    },
    green: {
        label: "Green",
        light: {
            accent: "#1F6B4F",
            accentHover: "#174F3A",
            accentSoft: "#E3F1EA",
            accentSofter: "#F2F8F5",
            onAccent: "#FFFFFF",
            chord: "#1F6B4F",
        },
        dark: {
            accent: "#7FD3A8",
            accentHover: "#A6E3C3",
            accentSoft: "#18352A",
            accentSofter: "#132A21",
            onAccent: "#0B2118",
            chord: "#8EDBB3",
        },
    },
    teal: {
        label: "Teal",
        light: {
            accent: "#0E7480",
            accentHover: "#0A5A63",
            accentSoft: "#E0F3F5",
            accentSofter: "#F1F9FA",
            onAccent: "#FFFFFF",
            chord: "#0E7480",
        },
        dark: {
            accent: "#6FD6E0",
            accentHover: "#9BE4EB",
            accentSoft: "#12363A",
            accentSofter: "#0F2A2D",
            onAccent: "#06262A",
            chord: "#85DEE6",
        },
    },
    purple: {
        label: "Purple",
        light: {
            accent: "#6B3FC4",
            accentHover: "#522F9A",
            accentSoft: "#F0EBFB",
            accentSofter: "#F8F5FE",
            onAccent: "#FFFFFF",
            chord: "#6B3FC4",
        },
        dark: {
            accent: "#B9A0FF",
            accentHover: "#D0C0FF",
            accentSoft: "#2E2550",
            accentSofter: "#221C3B",
            onAccent: "#1A1033",
            chord: "#C3ADFF",
        },
    },
    rose: {
        label: "Rose",
        light: {
            accent: "#B42358",
            accentHover: "#8E1B45",
            accentSoft: "#FBE8EF",
            accentSofter: "#FDF4F7",
            onAccent: "#FFFFFF",
            chord: "#B42358",
        },
        dark: {
            accent: "#FF96BA",
            accentHover: "#FFB7CF",
            accentSoft: "#43192A",
            accentSofter: "#31131F",
            onAccent: "#2E0A17",
            chord: "#FFA6C4",
        },
    },
    orange: {
        label: "Orange",
        light: {
            accent: "#B4520A",
            accentHover: "#8C3F06",
            accentSoft: "#FCEEDF",
            accentSofter: "#FEF7EF",
            onAccent: "#FFFFFF",
            chord: "#B4520A",
        },
        dark: {
            accent: "#FFB577",
            accentHover: "#FFCB9E",
            accentSoft: "#3E2814",
            accentSofter: "#2D1E10",
            onAccent: "#2A1505",
            chord: "#FFC08A",
        },
    },
} satisfies Record<string, Accent>;

/** The name of an accent colour, like `green`. */
export type AccentName = keyof typeof ACCENTS;
